import { create } from "zustand";

export interface Asset {
  id: string;
  img: string;
  flipHorizontal: boolean;
  flipVertical: boolean;
  zIndex: number;
  index: number;
}

export interface Background {
  id: string;
  img: string;
  zIndex: number;
  index: number;
}

export interface UploadAsset {
  id: string;
  img: string;
  ratio: number;
  flipHorizontal: boolean;
  flipVertical: boolean;
  zIndex: number;
  index: number;
}

export interface TextField {
  id: string;
  text: string;
  font: string;
  fontSize: number;
  startPosition?: {
    x: string;
    y: string;
  };
  width?: string;
  color?: string;
  zIndex: number;
  index: number;
}

export interface AssetsCount {
  background: number;
  baddies: number;
  base: number;
  borders: number;
  bullas: number;
  clothing: number;
  face: number;
  gallery: number;
  headgear: number;
  henlo: number;
  misc: number;
  puffpaw: number;
  template: number;
  thc: number;
}

interface StoreState {
  selectedImage: string;
  setSelectedImage: (image: string) => void;
  uploadedImage: string;
  setUploadedImage: (image: string) => void;
  assets: Asset[];
  setAssets: (assets: Asset[] | ((assets: Asset[]) => Asset[])) => void;
  updateAsset: (id: string, updates: Partial<Asset>) => void;
  background: Background | null;
  setBackground: (background: Background | null) => void;
  isDrawing: boolean;
  setIsDrawing: (isDrawing: boolean) => void;
  canDraw: boolean;
  setCanDraw: (canDraw: boolean) => void;
  strokeColor: string;
  setStrokeColor: (color: string) => void;
  strokeWidth: number;
  setStrokeWidth: (width: number) => void;
  tabs: "clothing" | "misc" | "head" | "face" | "eye" | "project";
  setTabs: (
    tabs: "clothing" | "misc" | "head" | "face" | "eye" | "project"
  ) => void;
  textFields: TextField[];
  addTextField: (textField: TextField) => void;
  updateTextField: (id: string, updates: Partial<TextField>) => void;
  removeTextField: (id: string) => void;
  flipImage: boolean;
  setFlipImage: (flip: boolean) => void;
  hideControls: boolean;
  setHideControls: (hide: boolean) => void;
  history: (string | null)[];
  setHistory: (history: (string | null)[]) => void;
  step: number;
  setStep: (step: number) => void;
  imageFormat: "banner" | "pfp" | "post" | null;
  setImageFormat: (format: "banner" | "pfp" | "post" | null) => void;
  ratio: string;
  setRatio: (ratio: string) => void;
  uploadAssets: UploadAsset[];
  setUploadAssets: (asset: UploadAsset[]) => void;
  updateUploadAsset: (id: string, updates: Partial<UploadAsset>) => void;
  setUploadAsset: (id: string, updates: Partial<UploadAsset>) => void;
  restrictedFonts: { value: string; label: string }[];
  setRestrictedFonts: (fonts: { value: string; label: string }[]) => void;
  local: boolean;
  setLocal: (local: boolean) => void;
  canvasIsLoading: boolean;
  setCanvasIsLoading: (loading: boolean) => void;
  templateIsLoading: boolean;
  setTemplateIsLoading: (loading: boolean) => void;
  assetsCount: AssetsCount;
  setAssetsCount: (count: AssetsCount) => void;
  hasUploadedGallery: boolean;
  setHasUploadedGallery: (hasUploadedGallery: boolean) => void;
  popupIsOpen: boolean;
  setPopupIsOpen: (isOpen: boolean) => void;
  isErasing: boolean;
  setIsErasing: (isErasing: boolean) => void;
}

const useUserStore = create<StoreState>((set) => ({
  selectedImage: "",
  uploadedImage: "",
  assets: [],
  background: null,
  isDrawing: false,
  canDraw: false,
  strokeColor: "#000000",
  strokeWidth: 4,
  tabs: "project",
  textFields: [],
  flipImage: false,
  hideControls: true,
  history: [null],
  step: 0,
  imageFormat: null,
  setSelectedImage: (image: string) => set({ selectedImage: image }),
  setUploadedImage: (image: string) => set({ uploadedImage: image }),
  setAssets: (assets: Asset[] | ((assets: Asset[]) => Asset[])) => {
    if (typeof assets === "function") {
      set((state) => ({ assets: assets(state.assets) }));
    } else {
      set({ assets });
    }
  },
  updateAsset: (id: string, updates: Partial<Asset>) =>
    set((state) => ({
      assets: state.assets.map((asset) =>
        asset.id === id ? { ...asset, ...updates } : asset
      ),
    })),
  setBackground: (background) => set({ background }),
  setIsDrawing: (isDrawing: boolean) => set({ isDrawing }),
  setCanDraw: (canDraw: boolean) => set({ canDraw }),
  setStrokeColor: (color: string) => set({ strokeColor: color }),
  setStrokeWidth: (width: number) => set({ strokeWidth: width }),
  setTabs: (tabs: "clothing" | "misc" | "head" | "face" | "eye" | "project") =>
    set({ tabs }),
  addTextField: (textField: TextField) =>
    set((state) => ({ textFields: [...state.textFields, textField] })),
  updateTextField: (id: string, updates: Partial<TextField>) =>
    set((state) => ({
      textFields: state.textFields.map((textField) =>
        textField.id === id ? { ...textField, ...updates } : textField
      ),
    })),
  removeTextField: (id: string) =>
    set((state) => ({
      textFields: state.textFields.filter((textField) => textField.id !== id),
    })),
  setFlipImage: (flip: boolean) => set({ flipImage: flip }),
  setHideControls: (hide: boolean) => set({ hideControls: hide }),
  setHistory: (history: (string | null)[]) => set({ history }),
  setStep: (step: number) => set({ step }),
  setImageFormat: (format: "banner" | "pfp" | "post" | null) =>
    set({ imageFormat: format }),
  ratio: "1",
  setRatio: (ratio: string) => set({ ratio }),
  uploadAssets: [],
  setUploadAssets: (asset: UploadAsset[]) => set({ uploadAssets: asset }),
  updateUploadAsset: (id: string, updates: Partial<UploadAsset>) =>
    set((state) => ({
      uploadAssets: state.uploadAssets.map((asset) =>
        asset.id === id ? { ...asset, ...updates } : asset
      ),
    })),
  setUploadAsset: (id: string, updates: Partial<UploadAsset>) =>
    set((state) => ({
      uploadAssets: state.uploadAssets.map((asset) =>
        asset.id === id ? { ...asset, ...updates } : asset
      ),
    })),
  restrictedFonts: [],
  setRestrictedFonts: (fonts: { value: string; label: string }[]) =>
    set({ restrictedFonts: fonts }),
  local: false,
  setLocal: (local: boolean) => set({ local }),
  canvasIsLoading: false,
  setCanvasIsLoading: (loading: boolean) => set({ canvasIsLoading: loading }),
  templateIsLoading: false,
  setTemplateIsLoading: (loading: boolean) =>
    set({ templateIsLoading: loading }),
  assetsCount: {
    background: 0,
    baddies: 0,
    base: 0,
    borders: 0,
    bullas: 0,
    clothing: 0,
    face: 0,
    gallery: 0,
    headgear: 0,
    henlo: 0,
    misc: 0,
    puffpaw: 0,
    template: 0,
    thc: 0,
  },
  setAssetsCount: (count: AssetsCount) => set({ assetsCount: count }),
  hasUploadedGallery: false,
  setHasUploadedGallery: (hasUploadedGallery: boolean) =>
    set({ hasUploadedGallery }),
  popupIsOpen: true,
  setPopupIsOpen: (isOpen: boolean) => set({ popupIsOpen: isOpen }),
  isErasing: false,
  setIsErasing: (isErasing: boolean) => set({ isErasing }),
}));

interface IndexState {
  lastTextFieldIndex: number;
  setLastTextFieldIndex: (index: number) => void;
  lastAssetIndex: number;
  setLastAssetIndex: (index: number) => void;
  lastUploadAssetIndex: number;
  setLastUploadAssetIndex: (index: number) => void;
  lastBackgroundIndex: number;
  setLastBackgroundIndex: (index: number) => void;
}

export const useIndexStore = create<IndexState>((set) => ({
  lastTextFieldIndex: 0,
  setLastTextFieldIndex: (index: number) => set({ lastTextFieldIndex: index }),
  lastAssetIndex: 0,
  setLastAssetIndex: (index: number) => set({ lastAssetIndex: index }),
  lastUploadAssetIndex: 0,
  setLastUploadAssetIndex: (index: number) =>
    set({ lastUploadAssetIndex: index }),
  lastBackgroundIndex: 0,
  setLastBackgroundIndex: (index) => set({ lastBackgroundIndex: index }),
}));

export default useUserStore;
